import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import withAdminAuth from "../../components/HOC/withAdminAuthPage";
import ChatHistoryWidget from "./ChatHistoryWidget";
import DatasetsWidget from "./DatasetsWidget";
import ModelsWidget from "./ModelsWidget";
import SchedulesWidget from "./SchedulesWidget";
import { AssetBreadCrumb, PageContainer } from "../../components/AssetBreadCrumb";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { fetchProjects } from "../../store/projectSlice";
import LoadingIcon from "../../components/LoadingIcon";
import { imageCache } from "../../utils/imageCache";
import { useAllMessagesSubscription, MCPMessage } from "../../hooks/useAllMessagesSubscription";
import { addProjectAiMessage, updateProjectAiMessage, selectProjectConversation } from "../../store/projectChatSlice";
import { ChatMessage } from "../../store/aiChatSlice";
import type { AppDispatch, RootState } from "../../store";

const ProjectDetailPage: React.FC = () => {
  const { projectid } = useParams<{ projectid: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [signedImageUrl, setSignedImageUrl] = useState<string | null>(null);
  const knownConversationIds = useSelector((state: RootState) => state.projectChat.knownConversationIds);
  const currentProjectConversationId = useSelector((state: RootState) => state.projectChat.currentConversation?.id);
  const projectTasks = useSelector((state: RootState) => state.projectChat.tasks);
  const projectTasksLoading = useSelector((state: RootState) => state.projectChat.tasksLoading);

  const { projects, isLoading } = useSelector((state: RootState) => state.projects);
  const project = projects.find((p) => p.id === projectid);

  // CANONICAL TASK SOURCE: projectChatSlice.tasks is the single source of truth for task display — it's kept in sync as tasks are added/completed/edited. The projects[n].tasks.items below is only a stale LIST_PROJECTS snapshot, used purely as a load-time fallback to avoid flashing 0. Do NOT read the snapshot for live counts elsewhere; reach for projectChatSlice.tasks instead.
  const taskCount =
    projectTasksLoading === "success"
      ? projectTasks.length
      : project?.tasks?.items?.length ?? 0;

  // Page-level subscription — receives AI replies for all project conversations
  useAllMessagesSubscription(
    useCallback((msg) => {
      if (!knownConversationIds.includes(msg.conversationId)) return;
      dispatch(addProjectAiMessage({
        conversationId: msg.conversationId,
        message: {
          id: msg.id,
          text: msg.content,
          isUser: msg.isUser,
          sessionId: msg.sessionId,
          inputTokens: msg.inputTokens,
          outputTokens: msg.outputTokens,
          executionTimeMs: msg.executionTimeMs,
          createdAt: msg.createdAt,
          status: msg.status,
        } as ChatMessage,
      }));
    }, [dispatch, knownConversationIds]),
    // On reconnect, refetch the open project conversation to catch any missed messages
    useCallback(() => {
      if (currentProjectConversationId) {
        dispatch(selectProjectConversation(currentProjectConversationId));
      }
    }, [dispatch, currentProjectConversationId]),
    // Live streaming: patch the message in place as <update> blocks arrive so reasoning shows live
    useCallback((msg: MCPMessage) => {
      if (!knownConversationIds.includes(msg.conversationId)) return;
      dispatch(updateProjectAiMessage({
        conversationId: msg.conversationId,
        messageId: msg.id,
        updates: {
          text: msg.content,
          status: msg.status,
          sessionId: msg.sessionId,
          inputTokens: msg.inputTokens,
          outputTokens: msg.outputTokens,
          executionTimeMs: msg.executionTimeMs,
        },
      }));
    }, [dispatch, knownConversationIds])
  );

  useEffect(() => {
    if (projects.length === 0) {
      dispatch(fetchProjects());
    }
  }, [dispatch, projects.length]);

  // Get signed URL for display — same pattern as GroupPage
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (project?.imageUrl) {
        try {
          const url = await imageCache.getSignedUrl(project.imageUrl);
          if (url) setSignedImageUrl(url);
        } catch (err) {
          console.error('[ProjectDetailPage] Error fetching signed image URL:', err);
        }
      } else {
        setSignedImageUrl(null);
      }
    };
    fetchSignedUrl();
  }, [project?.imageUrl]);

  const handleUploadImage = () => {
    if (!project) return;
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.projectImageUpload,
      properties: {
        id: project.id,
        name: project.title,
        imageUrl: project.imageUrl || null,
        organizationId: project.organizationId || null,
      },
    }));
  };

  const handleViewTasks = () => {
    if (!project) return;
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.viewProjectTasks,
      properties: {
        __modalSize: "md",
        projectId: project.id,
        projectTitle: project.title,
        tasks: projectTasks,
      },
    }));
  };


  if (!project && !isLoading) {
    return (
      <PageContainer fullHeight>
        <AssetBreadCrumb assetGroupName="Projects" />
        <div className="flex-1 flex items-center justify-center">
          <span className="text-gray-400 text-sm">Project not found.</span>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer fullHeight>
      <AssetBreadCrumb assetGroupName="Projects" assetName={project?.title} />
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">

        {/* Project Header */}
        <div className="shrink-0 p-6 border-b border-gray-100">
          <div className="flex items-start gap-5">

            {/* Profile image — click to change */}
            <div
              onClick={handleUploadImage}
              className="w-12 h-12 md:w-24 md:h-24 border border-gray-200 rounded-lg bg-eai-checklist-lt flex items-center justify-center overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              title="Change project image"
            >
              {signedImageUrl ? (
                <img
                  src={signedImageUrl}
                  alt={project?.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <BriefcaseIcon className="w-6 h-6 md:w-12 md:h-12 stroke-eai-primary-accent opacity-50" />
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="text-gray-900 text-xl font-semibold font-Inter mb-1">
                {project?.title}
              </div>
              <div className="text-gray-500 text-sm font-normal font-Inter max-w-xl leading-relaxed">
                {project?.description}
              </div>
            </div>

            {project && (
              <button onClick={handleViewTasks} className="eai-btn-grn text-sm flex-shrink-0 self-start">
                View Tasks ({taskCount})
              </button>
            )}
          </div>
        </div>

        {/* Widgets Area — desktop fills viewport (no page scroll), mobile scrolls */}
        <div className="flex-1 min-h-0 p-5 overflow-y-auto lg:overflow-hidden flex flex-col">
          <div className="flex-none text-xs font-semibold text-gray-400 uppercase tracking-widest mb-4">
            Project Overview
          </div>
          {isLoading || !project ? (
            <div className="flex items-center justify-center h-48">
              <LoadingIcon />
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[45fr_55fr] gap-4 lg:flex-1 lg:min-h-0">
            {/* Left (45%): Chat History + Schedules side-by-side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:min-h-0">
              <ChatHistoryWidget projectId={project.id} projectTitle={project.title} />
              <SchedulesWidget projectId={project.id} projectTitle={project.title} />
            </div>

            {/* Right (55%): Datasets + Models stacked */}
            <div className="flex flex-col gap-4 lg:min-h-0">
              <DatasetsWidget projectId={project.id} />
              <ModelsWidget />
            </div>
          </div>
          )}
        </div>

      </div>
    </PageContainer>
  );
};

export default withAdminAuth(ProjectDetailPage);
